import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Check, Clock, BookOpen, Wind, Heart, Sparkles, Moon, HeartHandshake, Waves } from "lucide-react";

const PRACTICE_ICONS = {
  "lectio-divina": BookOpen,
  "breath-prayer": Wind,
  "gratitude-reflection": Heart,
  "scripture-meditation": Sparkles,
  "examen": Moon,
  "intercessory-prayer": HeartHandshake,
  "silence-stillness": Waves,
};

function GuidedCard({ p, featured, done, delay }) {
  const Icon = PRACTICE_ICONS[p.slug] || BookOpen;
  return (
    <Link to={`/practices/guided/${p.slug}`} data-testid={`guided-card-${p.slug}`}
      className={`group rounded-md p-7 transition-colors duration-300 animate-fade-up border ${
        featured ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                 : "bg-card border-border hover:bg-muted/40"
      }`} style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start justify-between">
        <Icon className={`w-6 h-6 mb-6 ${featured ? "text-accent" : "text-accent"}`} />
        <div className="flex items-center gap-2">
          {done && (
            <span data-testid={`guided-done-badge-${p.slug}`} className={`w-6 h-6 rounded-full flex items-center justify-center ${featured ? "bg-primary-foreground/15" : "bg-primary/10"}`}>
              <Check className={`w-3.5 h-3.5 ${featured ? "text-primary-foreground" : "text-primary"}`} />
            </span>
          )}
          {featured && (
            <span data-testid="guided-featured-badge" className="text-[10px] uppercase tracking-[0.2em] bg-accent text-accent-foreground rounded-sm px-2.5 py-1">
              Today
            </span>
          )}
        </div>
      </div>
      <p className={`text-[11px] uppercase tracking-[0.18em] mb-1.5 ${featured ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{p.tagline}</p>
      <h3 className="font-heading text-2xl tracking-tight leading-tight">{p.name}</h3>
      <p className={`text-sm mt-3 leading-relaxed ${featured ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{p.description}</p>
      <div className="flex items-center justify-between mt-6">
        <span className={`flex items-center gap-1.5 text-xs ${featured ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          <Clock className="w-3.5 h-3.5" /> {p.duration_min} min
        </span>
        <ArrowRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 ${featured ? "text-primary-foreground/80" : "text-muted-foreground group-hover:text-primary"}`} />
      </div>
    </Link>
  );
}

export default function DevotionalList() {
  const [guided, setGuided] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/guided/list").then((r) => setGuided(r.data));
    api.get("/devotional/list").then((r) => setItems(r.data));
  }, []);

  const ordered = guided ? [...guided.practices].sort((a, b) => {
    if (a.slug === guided.featured) return -1;
    if (b.slug === guided.featured) return 1;
    return 0;
  }) : [];

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="animate-fade-up">
        <p className="label-overline mb-2">Guided Attunement</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">Practices</h1>
        <p className="text-muted-foreground mt-4 max-w-xl leading-relaxed">
          Ancient disciplines of the faith, guided step by step. A new practice is highlighted each day — or choose whichever your heart needs.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="guided-practices-grid">
        {ordered.map((p, i) => (
          <GuidedCard key={p.slug} p={p} featured={p.slug === guided.featured}
            done={guided.completed_today.includes(p.slug)} delay={i * 60} />
        ))}
      </div>

      <div className="mt-20 animate-fade-up">
        <p className="label-overline mb-2">Read & Reflect</p>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-none">Devotional practices</h2>
        <p className="text-muted-foreground mt-3 max-w-xl leading-relaxed">
          Quiet paths through a single passage — read, reflect, pray, and listen.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-10">
        {items.map((d, i) => (
          <Link key={d.id} to={`/practice/${d.id}`} data-testid={`practice-card-${d.id}`}
            className="group bg-card border border-border rounded-md p-8 hover:bg-muted/40 transition-colors duration-300 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <p className="label-overline mb-4">{d.theme}</p>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight">{d.title}</h3>
            <p className="font-scripture italic text-primary/80 mt-4 leading-relaxed">"{d.verse}"</p>
            <div className="flex items-center justify-between mt-6">
              <span className="text-sm text-muted-foreground">{d.verse_ref}</span>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all duration-300" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
